// =====================================================================
// PatientSummary (PME Sprint 2 / S51 · redesign S62) — "ficha digital" do
// paciente. Aba Resumo (padrão), read-only: header polido (status + contato +
// ações) → cards-resumo (próxima/última consulta, aniversário) → ALERGIAS em
// destaque → cartão "Dados do Paciente" com sub-navegação (Cadastrais/Contato/
// Anamnese) → últimas consultas → agenda. Pra editar -> botão Editar (Cadastro).
// =====================================================================
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Divider
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import EventNoteIcon from "@material-ui/icons/EventNote";
import DescriptionIcon from "@material-ui/icons/Description";
import AddIcon from "@material-ui/icons/Add";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PrintIcon from "@material-ui/icons/Print";
import EditIcon from "@material-ui/icons/Edit";
import PhoneIcon from "@material-ui/icons/Phone";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import HistoryIcon from "@material-ui/icons/History";
import CakeIcon from "@material-ui/icons/Cake";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";

import {
  listEncounters,
  listPatientAppointments,
  listEncounterProfessionals,
  ENCOUNTER_TYPE_LABELS,
  ENCOUNTER_STATUS_LABELS
} from "../../services/encounterService";
import PatientPhoto from "./PatientPhoto";

const useStyles = makeStyles((theme) => {
  const accent = theme.palette.primary.main;
  return {
    page: { padding: 4 },
    // ---------- header ----------
    header: {
      padding: theme.spacing(2.5, 3),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2.5),
      flexWrap: "wrap"
    },
    headInfo: { flex: 1, minWidth: 220 },
    nameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      flexWrap: "wrap"
    },
    name: { fontWeight: 700, lineHeight: 1.1 },
    statusBadge: {
      background: "#e6f4ea",
      color: "#1e8e3e",
      fontWeight: 600,
      height: 24
    },
    contactRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(0.5, 3),
      marginTop: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: 14
    },
    contactItem: { display: "flex", alignItems: "center", gap: 6 },
    actions: { display: "flex", alignItems: "center", gap: theme.spacing(0.5) },
    actionBtn: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      padding: 7
    },
    // ---------- cards-resumo ----------
    infoCard: {
      padding: theme.spacing(1.5, 2),
      borderRadius: 10,
      borderLeft: `4px solid ${accent}`,
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5)
    },
    infoIcon: { color: accent, fontSize: 30, opacity: 0.85 },
    infoLabel: {
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: theme.palette.text.secondary,
      fontWeight: 600
    },
    infoValue: { fontSize: 16, fontWeight: 600, lineHeight: 1.2 },
    infoSub: { fontSize: 12.5, color: theme.palette.text.secondary },
    // ---------- alergias ----------
    allergyCard: {
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      background: "rgba(220,38,38,0.08)",
      border: `1px solid ${theme.palette.error.main}`,
      borderLeft: `5px solid ${theme.palette.error.main}`
    },
    noAllergyCard: {
      padding: theme.spacing(1.25, 2),
      marginBottom: theme.spacing(2),
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      color: theme.palette.text.secondary
    },
    // ---------- cartão dados + sub-nav ----------
    dataCard: {
      borderRadius: 12,
      marginBottom: theme.spacing(2),
      display: "flex",
      flexWrap: "wrap",
      overflow: "hidden"
    },
    subNav: {
      width: 230,
      minWidth: 200,
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5),
      background: theme.palette.action.hover
    },
    subNavTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 700,
      color: accent,
      padding: theme.spacing(0.5, 1, 1.5, 1)
    },
    subNavItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      textAlign: "left",
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 8,
      marginBottom: 4,
      cursor: "pointer",
      border: "none",
      background: "transparent",
      color: theme.palette.text.primary,
      fontSize: 14,
      fontFamily: "inherit",
      transition: "all 0.15s",
      "&:hover": { background: theme.palette.action.selected }
    },
    subNavActive: {
      background: theme.palette.background.paper,
      color: accent,
      fontWeight: 600,
      boxShadow: `inset 3px 0 0 ${accent}`
    },
    dataContent: { flex: 1, minWidth: 260, padding: theme.spacing(3) },
    dataContentTitle: { fontWeight: 700, marginBottom: theme.spacing(2.5) },
    fieldLabel: {
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      color: theme.palette.text.secondary,
      fontWeight: 600,
      marginBottom: 2
    },
    fieldValue: { fontSize: 15, whiteSpace: "pre-wrap", paddingBottom: 4 },
    fieldUnderline: { borderBottom: `1px solid ${theme.palette.divider}` },
    // ---------- listas (consultas/agenda) ----------
    section: { padding: theme.spacing(2), marginBottom: theme.spacing(2), borderRadius: 12 },
    sectionHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5)
    },
    sectionTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1), fontWeight: 600 },
    row: {
      padding: theme.spacing(1.25, 1.5),
      marginBottom: theme.spacing(1),
      borderRadius: 8,
      cursor: "pointer",
      transition: "background 0.15s",
      "&:hover": { background: theme.palette.action.hover }
    },
    apptRow: { padding: theme.spacing(1, 1.5), marginBottom: theme.spacing(1), borderRadius: 8 },
    rowTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      flexWrap: "wrap"
    },
    meta: { color: theme.palette.text.secondary, fontSize: 13 },
    empty: { color: theme.palette.text.secondary, fontStyle: "italic" }
  };
});

const pad = (n) => String(n).padStart(2, "0");

const computeAge = (birthDate) => {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const habitsSummary = (h) => {
  if (!h || typeof h !== "object") return "";
  const parts = [];
  if (h.smoking) parts.push(`Tabagismo: ${h.smoking}`);
  if (h.alcohol) parts.push(`Álcool: ${h.alcohol}`);
  if (h.exercise) parts.push(`Exercício: ${h.exercise}`);
  if (h.sleep) parts.push(`Sono: ${h.sleep}`);
  return parts.join(" · ");
};

const addressSummary = (a) => {
  if (!a) return "";
  if (typeof a === "string") return a;
  if (typeof a === "object") {
    const parts = [a.street, a.number, a.district, a.city, a.state, a.zip].filter(Boolean);
    return parts.join(", ");
  }
  return "";
};

const apptStatusLabel = {
  scheduled: "Agendado",
  confirmed: "Confirmado",
  completed: "Realizado",
  cancelled: "Cancelado",
  no_show: "Não compareceu"
};

const Field = ({ label, value, classes, full }) => {
  if (!value || !String(value).trim()) return null;
  return (
    <Grid item xs={12} sm={full ? 12 : 6}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={`${classes.fieldValue} ${classes.fieldUnderline}`}>{value}</div>
    </Grid>
  );
};

const InfoCard = ({ classes, icon, label, value, sub }) => (
  <Grid item xs={12} sm={4}>
    <Paper variant="outlined" className={classes.infoCard}>
      {icon}
      <div style={{ minWidth: 0 }}>
        <div className={classes.infoLabel}>{label}</div>
        <div className={classes.infoValue}>{value}</div>
        {sub ? <div className={classes.infoSub}>{sub}</div> : null}
      </div>
    </Paper>
  </Grid>
);

const PatientSummary = ({ patient, onPhotoChange, onEditCadastro }) => {
  const classes = useStyles();
  const history = useHistory();

  const [encounters, setEncounters] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [profMap, setProfMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("dados"); // dados | contato | anamnese

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [encRes, profRes] = await Promise.all([
          listEncounters(patient.id),
          listEncounterProfessionals()
        ]);
        let appts = [];
        try {
          const ar = await listPatientAppointments(patient.id);
          appts = (ar && ar.appointments) || [];
        } catch (e) {
          /* agenda best-effort */
        }
        if (!active) return;
        const map = {};
        ((profRes && profRes.professionals) || []).forEach((p) => {
          map[String(p.id)] = p.name;
        });
        setProfMap(map);
        setEncounters((encRes && encRes.encounters) || []);
        setAppointments(appts);
      } catch (e) {
        /* resumo é tolerante — não derruba a tela */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [patient.id]);

  const age = computeAge(patient.birthDate);
  const hab = habitsSummary(patient.habits);
  const addr = addressSummary(patient.address);
  const hasAnamnese =
    (patient.allergies && patient.allergies.trim()) ||
    (patient.medicationsCurrent && patient.medicationsCurrent.trim()) ||
    (patient.familyHistory && patient.familyHistory.trim()) ||
    (patient.personalHistory && patient.personalHistory.trim()) ||
    hab ||
    (patient.notes && patient.notes.trim());

  // próxima consulta (agendada, futura) e última consulta (encounter mais recente)
  const now = new Date();
  const nextAppt = appointments
    .filter((a) => a.status === "scheduled" && new Date(a.datetimeStart) >= now)
    .sort((a, b) => new Date(a.datetimeStart) - new Date(b.datetimeStart))[0];
  const lastEnc = encounters[0];

  const whatsappPhone = onlyDigits(patient.phone);

  return (
    <div className={classes.page}>
      {/* ---------- HEADER ---------- */}
      <Paper className={classes.header} variant="outlined">
        <PatientPhoto
          patientId={patient.id}
          photoUrl={patient.photoUrl}
          size={92}
          editable
          onChange={onPhotoChange}
        />
        <div className={classes.headInfo}>
          <div className={classes.nameRow}>
            <Typography variant="h5" className={classes.name}>
              {patient.fullName}
            </Typography>
            <Chip
              size="small"
              label={patient.deletedAt ? "Inativo" : "Ativo"}
              className={classes.statusBadge}
              style={patient.deletedAt ? { background: "#fdecea", color: "#c5221f" } : undefined}
            />
            {patient.gender ? <Chip size="small" variant="outlined" label={patient.gender} /> : null}
            {age !== null ? <Chip size="small" variant="outlined" label={`${age} anos`} /> : null}
          </div>
          <div className={classes.contactRow}>
            <span className={classes.contactItem}>
              <PhoneIcon style={{ fontSize: 16 }} /> {patient.phone || "Não informado"}
            </span>
            {patient.email ? (
              <span className={classes.contactItem}>
                <MailOutlineIcon style={{ fontSize: 16 }} /> {patient.email}
              </span>
            ) : null}
            <span className={classes.contactItem}>🆔 CPF: {patient.cpf || "—"}</span>
          </div>
        </div>
        <div className={classes.actions}>
          {whatsappPhone ? (
            <Tooltip title="Abrir no WhatsApp">
              <IconButton
                className={classes.actionBtn}
                size="small"
                onClick={() => window.open(`https://wa.me/55${whatsappPhone}`, "_blank")}
              >
                <WhatsAppIcon fontSize="small" style={{ color: "#25D366" }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Imprimir ficha">
            <IconButton className={classes.actionBtn} size="small" onClick={() => window.print()}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onEditCadastro ? (
            <Tooltip title="Editar cadastro">
              <IconButton className={classes.actionBtn} size="small" onClick={onEditCadastro} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </Paper>

      {/* ---------- CARDS-RESUMO ---------- */}
      <Grid container spacing={2} style={{ marginBottom: 8 }}>
        <InfoCard
          classes={classes}
          icon={<EventAvailableIcon className={classes.infoIcon} />}
          label="Próxima consulta"
          value={nextAppt ? fmtDate(nextAppt.datetimeStart) : "Sem agendamento"}
          sub={nextAppt && nextAppt.professionalName ? nextAppt.professionalName : null}
        />
        <InfoCard
          classes={classes}
          icon={<HistoryIcon className={classes.infoIcon} />}
          label="Última consulta"
          value={lastEnc ? fmtDate(lastEnc.encounterDate) : "Nenhuma"}
          sub={
            lastEnc && lastEnc.professionalId && profMap[String(lastEnc.professionalId)]
              ? profMap[String(lastEnc.professionalId)]
              : null
          }
        />
        <InfoCard
          classes={classes}
          icon={<CakeIcon className={classes.infoIcon} />}
          label="Nascimento"
          value={patient.birthDate ? fmtDate(patient.birthDate) : "—"}
          sub={age !== null ? `${age} anos` : null}
        />
      </Grid>

      {/* ---------- ALERGIAS (destaque) ---------- */}
      {patient.allergies && patient.allergies.trim() ? (
        <Paper className={classes.allergyCard} variant="outlined">
          <WarningIcon style={{ color: "#dc2626" }} />
          <div>
            <div className={classes.fieldLabel} style={{ color: "#dc2626" }}>Alergias</div>
            <div className={classes.fieldValue} style={{ fontWeight: 600 }}>{patient.allergies}</div>
          </div>
        </Paper>
      ) : (
        <Paper className={classes.noAllergyCard} variant="outlined">
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="body2">Nenhuma alergia conhecida registrada.</Typography>
        </Paper>
      )}

      {/* ---------- CARTÃO DADOS + SUB-NAV ---------- */}
      <Paper className={classes.dataCard} variant="outlined">
        <div className={classes.subNav}>
          <div className={classes.subNavTitle}>
            <AssignmentIndIcon fontSize="small" /> Dados do Paciente
          </div>
          {[
            { key: "dados", label: "Dados Cadastrais", icon: <AssignmentIndIcon style={{ fontSize: 18 }} /> },
            { key: "contato", label: "Contato", icon: <ContactPhoneIcon style={{ fontSize: 18 }} /> },
            { key: "anamnese", label: "Anamnese", icon: <LocalHospitalIcon style={{ fontSize: 18 }} /> }
          ].map((it) => (
            <button
              key={it.key}
              className={`${classes.subNavItem} ${subTab === it.key ? classes.subNavActive : ""}`}
              onClick={() => setSubTab(it.key)}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>

        <div className={classes.dataContent}>
          {subTab === "dados" && (
            <>
              <Typography variant="h6" className={classes.dataContentTitle}>Dados Cadastrais</Typography>
              <Grid container spacing={3}>
                <Field label="Nome" value={patient.fullName} classes={classes} full />
                <Field label="Data de Nascimento" value={fmtDate(patient.birthDate)} classes={classes} />
                <Field label="Idade" value={age !== null ? `${age} anos` : "—"} classes={classes} />
                <Field label="Sexo" value={patient.gender} classes={classes} />
                <Field label="CPF" value={patient.cpf} classes={classes} />
                <Field label="RG" value={patient.rg} classes={classes} />
                <Field label="Cadastrado em" value={patient.createdAt ? fmtDate(patient.createdAt) : null} classes={classes} />
              </Grid>
            </>
          )}

          {subTab === "contato" && (
            <>
              <Typography variant="h6" className={classes.dataContentTitle}>Contato</Typography>
              <Grid container spacing={3}>
                <Field label="Telefone" value={patient.phone} classes={classes} />
                <Field label="E-mail" value={patient.email} classes={classes} />
                <Field label="Endereço" value={addr} classes={classes} full />
                {!patient.phone && !patient.email && !addr ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.empty}>Sem dados de contato. Edite na aba Cadastro.</Typography>
                  </Grid>
                ) : null}
              </Grid>
            </>
          )}

          {subTab === "anamnese" && (
            <>
              <Typography variant="h6" className={classes.dataContentTitle}>Anamnese</Typography>
              {hasAnamnese ? (
                <Grid container spacing={3}>
                  <Field label="Alergias" value={patient.allergies} classes={classes} />
                  <Field label="Medicações em uso" value={patient.medicationsCurrent} classes={classes} />
                  <Field label="Histórico familiar" value={patient.familyHistory} classes={classes} />
                  <Field label="Histórico pessoal" value={patient.personalHistory} classes={classes} />
                  <Field label="Hábitos" value={hab} classes={classes} full />
                  <Field label="Observações" value={patient.notes} classes={classes} full />
                </Grid>
              ) : (
                <Typography variant="body2" className={classes.empty}>
                  Anamnese ainda não preenchida. Edite na aba Cadastro.
                </Typography>
              )}
            </>
          )}
        </div>
      </Paper>

      {/* ---------- ÚLTIMAS CONSULTAS ---------- */}
      <Paper className={classes.section} variant="outlined">
        <div className={classes.sectionHeaderRow}>
          <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            <DescriptionIcon fontSize="small" /> Últimas consultas ({encounters.length})
          </Typography>
          <Button
            size="small"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push(`/prontuario/paciente/${patient.id}/consulta/nova`)}
          >
            Nova consulta
          </Button>
        </div>
        {loading ? (
          <CircularProgress size={22} />
        ) : encounters.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>Nenhuma consulta registrada ainda.</Typography>
        ) : (
          encounters.slice(0, 5).map((e) => (
            <Paper
              key={e.id}
              variant="outlined"
              className={classes.row}
              onClick={() => history.push(`/prontuario/consulta/${e.id}`)}
            >
              <div className={classes.rowTop}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {fmtDateTime(e.encounterDate)}
                </Typography>
                <Chip
                  size="small"
                  label={ENCOUNTER_STATUS_LABELS[e.status] || e.status}
                  style={e.status === "signed" ? { background: "#d1fae5", color: "#059669" } : undefined}
                  variant={e.status === "signed" ? "default" : "outlined"}
                />
              </div>
              <div className={classes.meta}>
                {ENCOUNTER_TYPE_LABELS[e.encounterType] || e.encounterType || "Consulta"}
                {e.professionalId && profMap[String(e.professionalId)] ? ` · ${profMap[String(e.professionalId)]}` : ""}
                {e.assessment ? ` · ${String(e.assessment).slice(0, 70)}${String(e.assessment).length > 70 ? "…" : ""}` : ""}
              </div>
            </Paper>
          ))
        )}
      </Paper>

      {/* ---------- AGENDA DO PACIENTE ---------- */}
      <Paper className={classes.section} variant="outlined">
        <div className={classes.sectionHeaderRow}>
          <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            <EventNoteIcon fontSize="small" /> Agenda do paciente ({appointments.length})
          </Typography>
          <Button size="small" onClick={() => history.push("/agenda-medica")}>
            Abrir agenda
          </Button>
        </div>
        {loading ? (
          <CircularProgress size={22} />
        ) : appointments.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>Nenhum agendamento na agenda médica.</Typography>
        ) : (
          appointments.slice(0, 4).map((a) => (
            <Paper key={a.id} variant="outlined" className={classes.apptRow}>
              <div className={classes.rowTop}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {fmtDateTime(a.datetimeStart)}
                </Typography>
                <Chip size="small" label={apptStatusLabel[a.status] || a.status || "—"} variant="outlined" />
              </div>
              <div className={classes.meta}>
                {a.professionalName ? a.professionalName : "—"}
                {a.procedureType ? ` · ${a.procedureType}` : ""}
                {a.paymentType ? ` · ${a.paymentType === "convenio" ? (a.insuranceName || "Convênio") : "Particular"}` : ""}
              </div>
            </Paper>
          ))
        )}
      </Paper>
    </div>
  );
};

export default PatientSummary;
